import { useGetListsWithoutProfilesQuery } from '@/api/listApi';
import { EntrySettings } from '@/api/types/entry-types';
import {
  getAccessLevelData,
  getCurrentAccessType,
  shouldHideLockIcon
} from '@/utils/privacy/accessCalculation';
import { AccessType } from '@/utils/privacy/types/AccessType';
import { useMemo } from 'react';

function hasCustomLists(accessType: AccessType | undefined | null): boolean {
  return (
    accessType === AccessType.allowCustomList ||
    accessType === AccessType.denyCustomList
  );
}

interface UseEntryAccessInfoProps {
  permissions: EntrySettings['permissions'];
  isCommunityEntry: boolean;
  canEdit: boolean;
}

export function useEntryAccessInfo({
  permissions,
  isCommunityEntry,
  canEdit
}: UseEntryAccessInfoProps) {
  const { accessSettingType, lists: listIds } = getCurrentAccessType({
    permissions
  });
  const shouldShowLockIcon = !shouldHideLockIcon(accessSettingType, canEdit);
  const label = getAccessLevelData(accessSettingType, isCommunityEntry);
  const shouldShowLists =
    shouldShowLockIcon && hasCustomLists(accessSettingType) && canEdit;

  // Load custom lists if needed
  const {
    data: lists,
    isLoading,
    error
  } = useGetListsWithoutProfilesQuery(undefined, {
    skip: !shouldShowLists
  });

  // Map custom list IDs to names for display
  const listNames = useMemo(() => {
    if (!listIds || !shouldShowLists) return null;
    return listIds.map(id => lists?.find(list => list.id === id)?.name || id);
  }, [lists, listIds, shouldShowLists]);

  return {
    label,
    listNames,
    isLoading,
    error,
    shouldShowLockIcon
  };
}
